// ============================================================================
// ROLE PERMISSION HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { rolePermissionService } from 'api/services/rolePermissionService';
import type {
  RolePermission,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
  RolePermissionFilterParams,
} from 'api/types/rolePermission.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'role-permissions';

export const useRolePermissions = (params?: RolePermissionFilterParams) => {
  const [data, setData] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRolePermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.getAll({ pageSize: 50000, ...params });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchRolePermissions();
  }, [fetchRolePermissions]);

  useDataChangeListener(DATA_KEY, fetchRolePermissions);

  return { data, loading, error, refetch: fetchRolePermissions };
};

export const useCreateRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRolePermission = useCallback(async (payload: CreateRolePermissionRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.create(payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createRolePermission, loading, error };
};

export const useUpdateRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRolePermission = useCallback(async (id: string, payload: UpdateRolePermissionRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.update(id, payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateRolePermission, loading, error };
};

export const useDeleteRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRolePermission = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await rolePermissionService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteRolePermission, loading, error };
};
